import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { DateTime } from "luxon";
import { Option, Select } from "@natera/form";
import { getTimeZones } from "@app/utils";
import { ErrorContext } from "@app/provider";
import { FormField } from "@app/components";

import "./timezoneSelector.scss";

const messages = defineMessages({
  timezoneSelectorPlaceholder: {
    id: "timezoneSelectorPlaceholder",
    defaultMessage: "Please select your time zone",
  },
  timezoneSelectorLabel: {
    id: "timezoneSelectorLabel",
    defaultMessage: "Time Zone",
  },
});

type Props = {
  value?: string;
  onSelect: (newValue: string) => void;
  defaultValue?: string;
  hideLabel?: boolean;
  variant?: "outlined" | "filled";
  showGmtOffset?: boolean;
};

function getGmtOffset(browserValue: string): string {
  const now = DateTime.now().setZone(browserValue);
  return `GMT${now.toFormat("ZZ")}`;
}

export const timezoneValidationName = "timezone";

const getTimezoneOptionId = (index: number) => `timezone-option-${index + 1}`;

const TimezoneSelector: React.FC<Props> = ({
  value,
  onSelect,
  defaultValue,
  hideLabel,
  variant = "outlined",
  showGmtOffset = false,
}) => {
  const intl = useIntl();

  const timeZoneHandler = (newValue: string) => onSelect(newValue);
  const { getValidationError } = React.useContext(ErrorContext);

  const timeZones = getTimeZones(intl);

  const formatLabel = (tz: { userValue: string; browserValue: string }) =>
    showGmtOffset
      ? `${tz.userValue} (${getGmtOffset(tz.browserValue)})`
      : tz.userValue;

  const selectElement = (
    <Select
      placeholder={intl.formatMessage(messages.timezoneSelectorPlaceholder)}
      labelId="timeZone"
      onValueChange={timeZoneHandler}
      outlined={variant === "outlined"}
      value={value}
      defaultValue={defaultValue}
      data-testid="timezone_selector"
      role="combobox"
      floating={variant === "outlined"}
    >
      {timeZones.map((timeZone, index) => (
        <Option
          key={timeZone.browserValue}
          id={getTimezoneOptionId(index)}
          value={timeZone.browserValue}
          data-testid={timeZone.browserValue}
          aria-selected={value === timeZone.browserValue}
        >
          {formatLabel(timeZone)}
        </Option>
      ))}
    </Select>
  );

  if (variant === "filled") {
    return <div className="timezone-selector--filled">{selectElement}</div>;
  }

  return (
    <FormField
      label={
        hideLabel
          ? undefined
          : intl.formatMessage(messages.timezoneSelectorLabel)
      }
      error={getValidationError(timezoneValidationName)}
      withPadding
      id="timeZone"
    >
      {selectElement}
    </FormField>
  );
};

export default TimezoneSelector;
